import React, { useEffect } from 'react';
import { useQuery } from 'react-query';
import { Box, Typography, Container as MuiContainer, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { detectBrowserLanguage } from 'i18n/detectBrowserLanguage';
import { getPublicOverview } from 'services/transparency-service';
import { getPublicBranding } from 'services/company-service';
import { TransparencyHeader } from './components/TransparencyHeader';
import { FrameworkBadges } from './components/FrameworkBadges';
import { GovernanceOverview } from './components/GovernanceOverview';
import { TrendIndicator } from './components/TrendIndicator';
import { PublicPolicies } from './components/PublicPolicies';
import { TransparencyPageContainer, TransparencyPageContent, SectionTitle } from './styles';

/**
 * PublicTransparencyPage
 * - No authentication required
 * - Accessed via /transparency/:companyKey (no token param)
 * - Shows high-level transparency information only
 * - noindex/nofollow enforced
 */
export const PublicTransparencyPage = ({ companyKey }) => {
  const { t } = useTranslation('transparency');

  // External visitors: force browser language regardless of localStorage/tenant default
  useEffect(() => {
    i18n.changeLanguage(detectBrowserLanguage());
  }, []);

  const { isLoading, error, data } = useQuery(
    ['publicTransparency', companyKey],
    () => getPublicOverview(companyKey),
    { enabled: !!companyKey }
  );

  const organizationData = data?.data;

  const { data: brandingData } = useQuery(
    ['publicBranding', companyKey],
    () => getPublicBranding(companyKey),
    { enabled: !!companyKey, retry: false, staleTime: 5 * 60 * 1000 }
  );

  useEffect(() => {
    const b = brandingData?.data;
    if (!b || b.default) return;
    const root = document.documentElement;
    if (b.primaryColor) root.style.setProperty('--color-primary', b.primaryColor);
    if (b.secondaryColor) root.style.setProperty('--color-secondary', b.secondaryColor);
    if (b.faviconUrl) {
      const favicon = document.querySelector('link[rel="icon"]');
      if (favicon) favicon.href = b.faviconUrl;
    }
    return () => {
      root.style.removeProperty('--color-primary');
      root.style.removeProperty('--color-secondary');
    };
  }, [brandingData]);

  // SEO: Prevent indexing (Story-008 requirement)
  // Must be called before any early returns to follow React hooks rules
  useEffect(() => {
    document.title = organizationData?.organization?.name || t('public.title');
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, [organizationData?.organization?.name, t]);

  if (isLoading) {
    return (
      <TransparencyPageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      </TransparencyPageContainer>
    );
  }

  if (error || !organizationData) {
    return (
      <TransparencyPageContainer>
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography variant="h5" color="text.secondary">
            {t('public.unavailableTitle')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('public.unavailableMessage')}
          </Typography>
        </Box>
      </TransparencyPageContainer>
    );
  }

  const { organization, frameworkBadges, trendIndicator, governanceStructure, policies } = organizationData;

  return (
    <TransparencyPageContainer>
      <TransparencyHeader
        name={organization?.name}
        description={organization?.description}
        lastUpdated={organization?.lastUpdated}
        brandingLogoUrl={brandingData?.data?.logoUrl}
      />

      <TransparencyPageContent>
        <MuiContainer maxWidth="lg">
          {trendIndicator && (
            <Box sx={{ mb: 4 }}>
              <TrendIndicator
                label={trendIndicator.label}
                description={trendIndicator.description}
                trend={trendIndicator.trend}
              />
            </Box>
          )}

          {frameworkBadges && frameworkBadges.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>{t('shared.commitments')}</SectionTitle>
              <FrameworkBadges badges={frameworkBadges} />
            </Box>
          )}

          {governanceStructure && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>{t('shared.governanceStructure')}</SectionTitle>
              <GovernanceOverview
                categories={governanceStructure.categories}
                overallPosture={governanceStructure.overallPosture}
              />
            </Box>
          )}

          {policies && policies.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <SectionTitle>{t('shared.publicPolicies')}</SectionTitle>
              <PublicPolicies policies={policies} />
            </Box>
          )}
        </MuiContainer>
      </TransparencyPageContent>
    </TransparencyPageContainer>
  );
};
